
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { get, head, intersection, isEmpty, isEqual, isNil, mapValues, some } from 'lodash';
import { toast } from 'react-toastify';

import { Typography, Grid, TextField, Box } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { useNavigate } from 'react-router-dom';
import LoadingMask from 'src/components/LoadingMask/LoadingMask';
import { useHandleKeyPress } from 'src/utils/Component/hooks/hooks';
import {
  useSelectedPlanId,
  useCurrentCommands,
  useSelectedTime,
  useCommandToDropdown,
  useCommandToTimeDropdown,
  useSelectedCommand,
  useHandleChangeCommand,
  useHandleChangeTime,
  DropdownProps,
} from 'src/utils/Component/hooks/PlanManagement.hooks';

import { ScopeReadyData, ServerScope, getScopeReadyData } from 'src/state/scope/Scope.types';
import { forceRefreshGrid, getScope, seedScope, balanceScope } from 'src/state/scope/Scope.actions';
import { getBalanceCommands, getSeedCommands, hasCommands, isCommandSeed } from 'src/state/scope/codecs/Commands.utils';
import { commandToKey, keyToCommand } from 'src/state/scope/codecs/projections/PlanMetadataToDropdown';
import { Command, CommandAnchorDetail, CommandDetail } from 'src/state/scope/codecs/Commands';
import { AvailableMembers } from 'src/services/Scope.client';
import { AppState, AppThunkDispatch } from 'src/store';
import { ReseedPlanModalOwnProps } from 'src/components/Mfp/Reseed/ReseedPlanModal';
import { SettingsByKey } from 'src/services/Settings';
import AnchorToLabel from './AnchorToLabel';
import './_InitializePlan.scss';
import { AutocompleteOption } from '../PlanMetadataGrid/PlanMetadataGrid.utils';
import { SeedInfoSelections } from '../MfpScopeSelector/MfpScopebar.types';
import { KeyboardArrowDown } from '@material-ui/icons';
import SquareActionButton from 'src/common-ui/components/Buttons/SquareActionButton';

// Function to check plans for mustSeed: true and no seed command for error state when plans are invalid
function hasInvalidPlans(readyScope: ScopeReadyData | undefined): boolean {
  // Return true if any plan is invalid in commands array
  return some(readyScope?.commands, (plan) => {
    if (plan.mustSeed) {
      // The plan is invalid if it does NOT have a seed command when mustSeed is true
      const hasSeedCommand = some(plan.commands, isCommandSeed);
      return !hasSeedCommand; // Invalid if no seed command
    }
    // If mustSeed is false, the plan is considered valid
    return false;
  });
}

const mapStateToProps = (state: AppState) => {
  const { mfpScope: scope, settings } = state;
  const dimensionLabel = settings.dimensionLabelProperty;
  const readyScope = getScopeReadyData(scope);

  if (!readyScope || isNil(dimensionLabel) || !state.viewConfigSlice.availableMembers || isEmpty(readyScope.commands)) {
    return {
      scopeId: undefined,
      uninitializedPlans: undefined,
      balanceCommands: undefined,
      seedCommands: undefined,
      dimensionLabel: undefined,
      availableMembers: undefined,
      showEOP: undefined,
      isMultiScope: undefined,
      entries: undefined,
    };
  }

  // map these to id to compare to `currentAnchors` later
  const availableMemberIds = mapValues(state.viewConfigSlice.availableMembers.space, (dim) => dim.map((mem) => mem.id));
  const currentAnchors = readyScope.currentAnchors!; // we check for the existence of this above, so this ! is safe

  /** Indicates whether some permissions exist in the created scope but not `availableMembers` */
  const hasInvalidAnchors = !isEqual(
    mapValues(currentAnchors, (v, key) => {
      // here we create an intersection of the `currentAnchor` ids and the `availableMemberIds` ids,
      // ideally to show that everything in `currentAnchors` also appears as an id in `availableMemberIds`,
      // if the intersection of the two isn't equal to `currentAnchor`, then the user doesn't have permissions to
      // some product or location, and we can't let them login
      return intersection(v, availableMemberIds[key]);
    }),
    currentAnchors
  );

  // Permissions issue
  if (readyScope && hasInvalidAnchors) {
    return {
      anchorError: true,
    };
  }
  // Seeding Issue
  if (readyScope && hasInvalidPlans(readyScope)) {
    return {
      seedingError: true,
    };
  }

  // map it twice, as hasCommands doesn't return the correct type
  // filter so that we don't get empty sets of commands
  const balanceCommands = readyScope.commands.map(getBalanceCommands).filter(hasCommands).map(getBalanceCommands);
  const seedCommands = readyScope.commands.map(getSeedCommands).filter(hasCommands).map(getSeedCommands);
  const showEOP = !isEmpty(balanceCommands);

  return {
    scopeId: readyScope.mainConfig.id,
    uninitializedPlans: readyScope.mainConfig.uninitializedPlans,
    balanceCommands,
    seedCommands,
    dimensionLabel,
    availableMembers: state.viewConfigSlice.availableMembers,
    showEOP,
    isMultiScope: readyScope.isMultiScope,
    entries: settings.entriesByKey,
  };
};

const mapDispatchToProps = (dispatch: AppThunkDispatch) => {
  return {
    disaptchedSeedScope: (seedCommand: Command['command']) => dispatch(seedScope(seedCommand)),
    dispatchedBalanceScope: (balanceCommand: Command['command']) => {
      return dispatch(balanceScope(balanceCommand)).then(() => dispatch(forceRefreshGrid()));
    },
    getScope: (scopeId: string) => dispatch(getScope({ scopeId })),
  };
};

export interface InitializePlanPropsValueProps {
  seedCommands?: CommandAnchorDetail[];
  balanceCommands?: CommandAnchorDetail[];
  scopeId?: string;
  uninitializedPlans?: ServerScope['uninitializedPlans'];
  dimensionLabel?: string;
  availableMembers?: AvailableMembers;
  showEOP?: boolean;
  isMultiScope?: boolean;
  entries?: SettingsByKey;
}
export type InitializePlanPropsDispatchProps = ReturnType<typeof mapDispatchToProps>;
type InitializePlanProps = ReseedPlanModalOwnProps &
  InitializePlanPropsValueProps &
  InitializePlanPropsDispatchProps & {
    balanceTime: string;
    copyVersion: string;
    copyVersionOptions: AutocompleteOption[];
    onItemChange: (field: SeedInfoSelections, value?: number | string) => void;
    seedingError: boolean;
    anchorError: boolean;
  };

interface Step extends CommandDetail {
  active: boolean;
}

const InitializePlan = (props: InitializePlanProps) => {
  const {
    scopeId,
    loading,
    balanceCommands,
    seedCommands,
    uninitializedPlans,
    onCancel,
    onSubmit,
    getScope: dispatchGetScope,
    disaptchedSeedScope,
    dispatchedBalanceScope,
    showEOP,
    entries,
    seedingError,
    anchorError,
  } = props;
  const [mutationPending, setMutationPending] = useState(false);

  const [selectedPlanId, setSelectedPlanId] = useSelectedPlanId(seedCommands);
  const currentBalanceCommands = useCurrentCommands(balanceCommands, selectedPlanId);
  const [selectedBalanceTime, setSelectedBalanceTime] = useSelectedTime(currentBalanceCommands);
  const balanceVersionOptions = useCommandToDropdown(balanceCommands, selectedPlanId, selectedBalanceTime, entries);
  const balanceTimeOptions = useCommandToTimeDropdown(currentBalanceCommands, entries);
  const [selectedBalanceCommand, setSelectedBalanceCommand] = useSelectedCommand(currentBalanceCommands);
  const handleChangeBalanceCommand = useHandleChangeCommand(setSelectedBalanceCommand);
  const handleChangeBalanceTime = useHandleChangeTime(selectedBalanceTime, setSelectedBalanceTime);

  const currentSeedCommands = useCurrentCommands(seedCommands, selectedPlanId);
  const [selectedSeedTime, setSelectedSeedTime] = useSelectedTime(currentSeedCommands);
  const seedVersionOptions = useCommandToDropdown(seedCommands, selectedPlanId, selectedSeedTime, entries);
  const seedTimeOptions = useCommandToTimeDropdown(currentSeedCommands, entries);
  const [selectedSeedCommand, setSelectedSeedCommand] = useSelectedCommand(currentSeedCommands);
  const handleChangeSeedCommand = useHandleChangeCommand(setSelectedSeedCommand);
  const handleChangeSeedTime = useHandleChangeTime(selectedSeedTime, setSelectedSeedTime);
  useEffect(() => {
    // When options arrive and nothing is selected yet, auto-select the first option
    if (!selectedSeedTime && seedTimeOptions.length > 0) {
      setSelectedSeedTime(seedTimeOptions[0].value as string);
    }
  }, [selectedSeedTime, seedTimeOptions, setSelectedSeedTime]);
  useEffect(() => {
    // Auto-select first version when options ready and nothing chosen
    if (!selectedSeedCommand && seedVersionOptions.length > 0) {
      setSelectedSeedCommand(seedVersionOptions[0].value as string);
    }
  }, [selectedSeedCommand, seedVersionOptions, setSelectedSeedCommand]);

  useEffect(() => {
    if (!selectedSeedTime && currentSeedCommands) {
      setSelectedSeedTime(head(currentSeedCommands.commands)?.displayTime);
    }
  }, [currentSeedCommands, selectedSeedTime, setSelectedSeedTime]);

  useEffect(() => {
    if (!selectedPlanId) {
      const planId = head(seedCommands)?.planId || head(balanceCommands)?.planId;
      setSelectedPlanId(planId);
    }
  }, [balanceCommands, seedCommands, selectedPlanId, setSelectedPlanId]);
  useEffect(() => {
    if (!selectedSeedCommand && currentSeedCommands) {
      setSelectedSeedCommand(commandToKey(head(currentSeedCommands.commands)!));
    }
  }, [
    balanceCommands,
    currentSeedCommands,
    selectedPlanId,
    selectedSeedCommand,
    setSelectedPlanId,
    setSelectedSeedCommand,
  ]);
  useEffect(() => {
    if (!selectedBalanceTime && currentBalanceCommands) {
      setSelectedBalanceTime(head(currentBalanceCommands.commands)?.displayTime);
    }
  }, [selectedBalanceTime, currentBalanceCommands]);

  const [steps, setSteps] = useState<Step[]>([]);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (!seedCommands || !balanceCommands) return undefined;
    setSteps(
      [...seedCommands, ...balanceCommands].map((s, idx) => {
        if (idx === activeStep) {
          setSelectedPlanId(s.planId);
        }
        return {
          ...s,
          active: idx === activeStep,
        };
      })
    );
  }, [activeStep, balanceCommands, seedCommands, setSelectedPlanId]);

  /**
   * If this function returns `true`, then the user has completed all steps, and we can exit the modal
   */
  const changeStep = useCallback(
    (change: number) => {
      if (!steps) return false;
      const nextStep = activeStep + change;
      if (nextStep > steps.length) return true;
      setActiveStep(nextStep);
      return nextStep >= steps.length;
    },
    [activeStep, steps]
  );

  const handlePreviousButton = () => changeStep(-1);

  const handleNextButtonOnClick = useCallback(async () => {
    if (currentSeedCommands && selectedSeedCommand) {
      const commandsAtTime = currentSeedCommands.commands.filter((c) => c.displayTime === selectedSeedTime);
      const maybeFoundCommand = keyToCommand(selectedSeedCommand, commandsAtTime);

      if (!maybeFoundCommand) return;
      setMutationPending(true);
      const maybeSeed = await disaptchedSeedScope(maybeFoundCommand.command);

      if (maybeSeed.type === seedScope.rejected.type) {
        // failure, toast and don't go forward
        toast.error('An error occured seeding your scope');
      } else {
        if (changeStep(1) && !showEOP) {
          dispatchGetScope(scopeId!).then(() => {
            onSubmit();
          });
        }
      }
      setMutationPending(false);
    }
  }, [
    currentSeedCommands,
    selectedSeedCommand,
    disaptchedSeedScope,
    showEOP,
    changeStep,
    dispatchGetScope,
    scopeId,
    onSubmit,
    selectedSeedTime,
  ]);

  const handleSubmitBalance = useCallback(async () => {
    if (currentBalanceCommands && selectedBalanceCommand) {
      setMutationPending(true);
      const maybeFoundCommand = keyToCommand(selectedBalanceCommand, currentBalanceCommands.commands);
      if (!maybeFoundCommand) {
        return;
      }
      await dispatchedBalanceScope(maybeFoundCommand.command)
        .catch(() => {
          toast.error('An error occured copying EOP to BOP');
          throw new Error('An error occured copying EOP to BOP');
        })
        .then(() => {
          if (changeStep(1)) {
            dispatchGetScope(scopeId!).then(() => {
              onSubmit();
            });
          }
        })
        .finally(() => setMutationPending(false));
    }
  }, [
    currentBalanceCommands,
    selectedBalanceCommand,
    dispatchedBalanceScope,
    dispatchGetScope,
    scopeId,
    onSubmit,
    changeStep,
  ]);
  const handleEnterPress = useHandleKeyPress(handleSubmitBalance);

  // For the Back button on error
  const navigate = useNavigate();
  const handleBackButton = () => navigate('/');

  return (
    <div>
      <div className="initialize-plan">
        {seedingError || anchorError ? (
          <div className="error-message">
            {seedingError && <p>Seeding is required, but no valid seed data could be found. Please contact support.</p>}
            {anchorError && <p>Your account lacks permissions to the created resources; please contact support.</p>}
            <SquareActionButton
              onClick={handleBackButton}
              dataQa="initialize-btn-back"
              style={{ display: 'block', margin: '20px auto' }}
              text="BACK"
              type="secondary"
            />
          </div>
        ) : (
          <>
            <Grid container spacing={2}>
              {isNil(uninitializedPlans) || isNil(balanceCommands) || isNil(seedCommands) || isEmpty(steps) ? (
                <div>
                  Please wait while your plan is prepared
                  <LoadingMask coverAll={true} />
                </div>
              ) : null}

              <Grid item xs={12}>
                {steps.map((step, stepIdx) => {
                  if (step.commands.every((c) => c.type === 'seed')) {
                    return (
                      <SeedSegment
                        key={`seed${stepIdx}`}
                        loading={loading}
                        command={step}
                        idx={`seed${stepIdx}`}
                        seedTimeOptions={seedTimeOptions}
                        selectedSeedTime={selectedSeedTime}
                        seedVersionOptions={seedVersionOptions}
                        selectedSeedCommand={selectedSeedCommand}
                        handleChangeSeedCommand={handleChangeSeedCommand}
                        handleChangeTime={handleChangeSeedTime}
                      />
                    );
                  } else if (step.commands.every((s, idx) => s.type === 'balance')) {
                    return (
                      <BalanceSegment
                        key={`balance${stepIdx}`}
                        loading={loading}
                        command={step}
                        idx={`balance${stepIdx}`}
                        balanceTimeOptions={balanceTimeOptions}
                        selectedBalanceTime={selectedBalanceTime}
                        handleChangeBalanceCommand={handleChangeBalanceCommand}
                        balanceVersionOptions={balanceVersionOptions}
                        selectedBalanceCommand={selectedBalanceCommand}
                        handleChangeBalanceTime={handleChangeBalanceTime}
                      />
                    );
                  }
                  return null;
                })}
              </Grid>
            </Grid>

            <div className='initialize-plan-modal-footer'>
              <SquareActionButton
                className="button"
                type="secondary"
                onClick={onCancel}
                dataQa="initialize-btn-cancel"
                text="CANCEL"
              />
              <SquareActionButton
                type="secondary"
                onClick={handlePreviousButton}
                dataQa="initialize-btn-previous"
                className="button"
                disabled={activeStep === 0}
                style={{ marginLeft: '0.5rem' }}
                text="PREVIOUS"
              />

              <SquareActionButton
                type="primary"
                dataQa="initialize-btn-next"
                className={'initialize-plan-modal-button button'}
                style={{
                  marginLeft: '0.5rem',
                  display: get(steps, `${activeStep}`)?.commands[0].type === 'seed' ? '' : 'none',
                }}
                onClick={handleNextButtonOnClick}
                disabled={mutationPending}
                loading={mutationPending}
                text={mutationPending ? 'Loading...' : 'NEXT'}
              />

              <SquareActionButton
                type="primary"
                dataQa="initialize-btn-submit"
                className={'initialize-plan-modal-button button'}
                style={{
                  marginLeft: '0.5rem',
                  display: get(steps, `${activeStep}`)?.commands[0].type === 'balance' ? '' : 'none',
                }}
                onClick={handleSubmitBalance}
                onKeyPress={handleEnterPress}
                disabled={mutationPending}
                loading={mutationPending}
                text={steps?.length - 1 ? 'COMPLETE' : 'NEXT'}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const SeedSegment = (props: {
  loading: boolean;
  command: Step;
  idx: string;
  selectedSeedTime: string | undefined;
  seedTimeOptions: DropdownProps[];
  handleChangeTime: (event: React.ChangeEvent<{}>, data: DropdownProps | null) => void;
  selectedSeedCommand: string | undefined;
  seedVersionOptions: DropdownProps[];
  handleChangeSeedCommand: (_event: React.ChangeEvent<{}>, data: DropdownProps | null) => void;
}) => {
  const {
    loading,
    command,
    idx,
    selectedSeedTime,
    seedTimeOptions,
    handleChangeTime,
    selectedSeedCommand,
    seedVersionOptions,
    handleChangeSeedCommand,
  } = props;

  const selectedSeedTimeOption =
    seedTimeOptions.find((o) => o.value === selectedSeedTime) ??
    (seedTimeOptions.length > 0 ? seedTimeOptions[0] : null);
  const selectedSeedVersionOption =
    seedVersionOptions.find((o) => o.value === selectedSeedCommand) ??
    (seedVersionOptions.length > 0 ? seedVersionOptions[0] : null);

  return (
    <Box key={idx} className={command.active ? '' : 'init-hide'} mb={2}>
      <Typography variant="h6" className="initialize-plan-header">
        Seed the Plan for:
        <div className="initialize-plan-header-text">
          <ul>
            <AnchorToLabel anchor={command.anchor} />
          </ul>
        </div>
      </Typography>

      <Box className="dropdown-group" mt={2}>
        <div className="dropdown-group-label">Select the Seed Basis Plan Period</div>
        <Autocomplete
          fullWidth
          loading={loading}
          disabled={seedTimeOptions.length === 0}
          options={seedTimeOptions}
          className="no-border-style-input"
          popupIcon={<KeyboardArrowDown className="teal-arrow" />}
          getOptionLabel={(option) => option.text ?? ''}
          value={selectedSeedTimeOption}
          onChange={handleChangeTime}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              InputProps={{
                ...params.InputProps,
              }}
            />
          )}
        />
      </Box>

      <Box className="dropdown-group" mt={2}>
        <div className="dropdown-group-label">Select the Seed Basis Plan Version</div>
        <Autocomplete
          fullWidth
          loading={loading}
          className="no-border-style-input"
          popupIcon={<KeyboardArrowDown className="teal-arrow" />}
          disabled={seedVersionOptions.length === 0}
          options={seedVersionOptions}
          getOptionLabel={(option) => option.text ?? ''}
          value={selectedSeedVersionOption}
          onChange={handleChangeSeedCommand}
          renderInput={(params) => <TextField {...params} variant="outlined" />}
        />
      </Box>
    </Box>
  );
};

function BalanceSegment(props: {
  loading: boolean;
  command: Step;
  idx: string;
  balanceTimeOptions: DropdownProps[];
  selectedBalanceTime: string | undefined;
  handleChangeBalanceCommand: (e: React.ChangeEvent<{}>, data: DropdownProps | null) => void;
  handleChangeBalanceTime: (e: React.ChangeEvent<{}>, data: DropdownProps | null) => void;
  balanceVersionOptions: DropdownProps[];
  selectedBalanceCommand: string | undefined;
}) {
  const {
    loading,
    command,
    idx,
    balanceTimeOptions,
    selectedBalanceTime,
    handleChangeBalanceCommand,
    handleChangeBalanceTime,
    balanceVersionOptions,
    selectedBalanceCommand,
  } = props;

  return (
    <Box key={idx} className={command.active ? '' : 'init-hide'} mb={2}>
      <Typography variant="h6" className="initialize-plan-header">
        Copy EOP to BOP for:
        <div className="initialize-plan-header-text">
          <ul>
            <AnchorToLabel anchor={command.anchor} />
          </ul>
        </div>
      </Typography>

      <Box className="dropdown-group" mt={2}>
        <div className="dropdown-group-label">Select the Plan period to Copy From</div>
        <Autocomplete
          fullWidth
          loading={loading}
          options={balanceTimeOptions}
          disabled={true}
          className="no-border-style-input"
          popupIcon={<KeyboardArrowDown className="teal-arrow" />}
          getOptionLabel={(option) => option.text ?? ''}
          value={balanceTimeOptions.find((o) => o.value === selectedBalanceTime) || null}
          onChange={handleChangeBalanceTime}
          renderInput={(params) => <TextField {...params} variant="outlined" />}
        />
      </Box>

      <Box className="dropdown-group" mt={2}>
        <div className="dropdown-group-label">Select the Plan Version</div>
        <Autocomplete
          fullWidth
          loading={loading}
          options={balanceVersionOptions}
          className="no-border-style-input"
          popupIcon={<KeyboardArrowDown className="teal-arrow" />}
          getOptionLabel={(option) => option.text ?? ''}
          value={balanceVersionOptions.find((o) => o.value === selectedBalanceCommand) || undefined}
          onChange={handleChangeBalanceCommand}
          renderInput={(params) => <TextField {...params} variant="outlined" />}
        />
      </Box>
    </Box>
  );
}

// @ts-ignore
export default connect(mapStateToProps, mapDispatchToProps)(InitializePlan);
